import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Modal } from "react-bootstrap";
import MultiTable from "../../../Common/MUITable";
import RcInnerFilter from "./RcInnerFilter";
import DelhiReportExport from "./DelhiReportExport";
import { GetTransferStations, RcDeployeVehicleInner } from "./ReportApi";
import { getTodayEndDateTime, getTodayStartDateTime } from "./reportCommon";

const formatToDDMMYYYY = (dateStr) => {
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split("T")[0].split("-");
  if (!year || !month || !day) return dateStr;
  return `${day}-${month}-${year}`;
};

const thumbnailImageStyle = {
  width: 28,
  height: 28,
  objectFit: "cover",
  borderRadius: 4,
  display: "block",
  margin: "0 auto",
  cursor: "pointer",
};

const exportColumns = [
  { label: "Ward", field: "wardName" },
  { label: "DHALAO", field: "comingFromDhalao" },
  { label: "Vehicle No", field: "vehicleNo" },
  { label: "Dump Site Name", field: "dumpSiteName" },
  { label: "Reported Date", field: "reportedDate" },
  { label: "Time", field: "reportedTime" },
  {
    label: "Tonnage",
    children: [
      { label: "Gross Weighment", field: "grossWeighment" },
      { label: "Tare Weighment", field: "tareWeighment" },
      { label: "Net Weighment", field: "netWeighment" },
    ],
  },
];

const RcInnerReport = () => {
  const navigate = useNavigate();
  const location = useLocation();
const parentRow = location.state?.parentRow;


const circleId =
  location.state?.circleId ||
  parentRow?.circleId ||
  "";

// The outer report's date filter is a plain "YYYY-MM-DD" value (no time),
// but this report's API expects a full datetime — pad it to start of day, or
// to the current time of day for the end (never later than "now", since the
// date & time inputs here cap out at the current moment).
const toDateTimeRangeValue = (dateStr, isEnd) => {
  if (!dateStr) return isEnd ? getTodayEndDateTime() : getTodayStartDateTime();
  if (dateStr.includes("T")) return dateStr;
  return isEnd
    ? `${dateStr}T${getTodayEndDateTime().split("T")[1]}`
    : `${dateStr}T00:00`;
};

const fromDate = toDateTimeRangeValue(location.state?.fromDate, false);

const toDate = toDateTimeRangeValue(location.state?.toDate, true);

const [filters, setFilters] = useState({
  circleId: circleId || "",
  vehicleNumber: "",
  site: "",
  fromDateTime: fromDate || "",
  toDateTime: toDate || "",
});

const [appliedFilters, setAppliedFilters] = useState({
  circleId: circleId || "",
  vehicleNumber: "",
  site: "",
  fromDateTime: fromDate || "",
  toDateTime: toDate || "",
});
const [reportData, setReportData] = useState([]);
const [loading, setLoading] = useState(false);
const [previewImage, setPreviewImage] = useState("");
const [siteOptions, setSiteOptions] = useState([]);

useEffect(() => {
  const fetchSiteOptions = async () => {
    try {
      const response = await GetTransferStations();
      const stations = response?.data?.data || [];
      setSiteOptions(stations);
    } catch (error) {
      console.error("Error fetching transfer stations:", error);
      setSiteOptions([]);
    }
  };

  fetchSiteOptions();
}, []);

const tableColumns = [
  {
    label: "Ward",
    field: "wardName",
    width: "90px",
  },
  {
    label: "DHALAO",
    field: "comingFromDhalao",
    width: "300px",
  },
  {
    label: "Vehicle No",
    field: "vehicleNo",
    width: "120px",
  },
  {
    label: "Dump Site Name",
    field: "dumpSiteName",
    width: "120px",
  },
  {
    label: "Reported Date",
    field: "reportedDate",
    width: "110px",
  },
  {
    label: "Time",
    field: "reportedTime",
    width: "80px",
  },
  {
    label: "Tonnage",
    children: [
      {
        label: "Gross Weighment",
        field: "grossWeighment",
        width: "110px",
      },
      {
        label: "Tare Weighment",
        field: "tareWeighment",
        width: "110px",
      },
      {
        label: "Net Weighment",
        field: "netWeighment",
        width: "110px",
      },
    ],
  },
  {
    label: "Pic of WB Slip",
    field: "picOfWbSlip",
    width: "110px",
    render: (row) =>
      row.picOfWbSlip ? (
        <img
          src={row.picOfWbSlip}
          alt="WB Slip"
          onClick={() => setPreviewImage(row.picOfWbSlip)}
          style={thumbnailImageStyle}
        />
      ) : (
        "N/A"
      ),
  },
];

useEffect(() => {
  const fetchInnerReport = async () => {
    try {
      setLoading(true);

    const response = await RcDeployeVehicleInner({
  circleId: circleId,
  vehicleNumber: appliedFilters.vehicleNumber,
  site: appliedFilters.site,
  fromDateTime: appliedFilters.fromDateTime,
  toDateTime: appliedFilters.toDateTime,
  fromDate: fromDate,
  toDate: toDate,
});

      console.log("INNER REPORT RESPONSE:", response);

   const data = response?.data?.data || [];

const mappedData = data.map((row) => ({
  ...row,

  wardName: parentRow?.wardName || "",

  comingFromDhalao: row.Dhalau || "",

  vehicleNo: row["Vehicle No"] || "",

  dumpSiteName: row["Dump Site Name"] || "",

  reportedDate: row["Reported Date"]
    ? formatToDDMMYYYY(row["Reported Date"])
    : "",

  reportedTime: row.Time || "",

  grossWeighment: row["Gross Weight"] ?? "",

  tareWeighment: row["Tare Weight"] ?? "",

  netWeighment: row["Net Weight"] ?? "",

  picOfWbSlip: row["Pic of WB Slip"] || "",
}));

console.log("MAPPED INNER DATA:", mappedData);

setReportData(mappedData);
    } catch (error) {
      console.error("Error fetching inner report:", error);
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  if (circleId) {
    fetchInnerReport();
  }
}, [circleId, appliedFilters]);


const handleSearch = (values) => {
  setAppliedFilters({
    ...values,
    circleId: circleId,
  });
};

 const handleReset = () => {
  const resetValues = {
    circleId: circleId || "",
    vehicleNumber: "",
    site: "",
    fromDateTime: fromDate || "",
    toDateTime: toDate || "",
  };

  setFilters(resetValues);
  setAppliedFilters(resetValues);
};

  return (
    <div className="content text-start">
      <div className="page-content">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-3 mt-3 mb-3">
          <div className="me-md-3">
            <h5 className="mb-1 fw-semibold">RC Inner Report</h5>
            <p className="text-muted mb-0">
              View trip details for W2E and dumpsite.
              {parentRow?.wardName ? ` (${parentRow.wardName})` : ""}
            </p>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <button
              type="button"
              className="button-container"
              onClick={() => navigate("/rc-deployment-report")}
            >
              Back
            </button>
            <DelhiReportExport
              reportData={reportData}
              reportTitle="RC Inner Report"
              columns={exportColumns}
              fileName="RcInnerReport"
            />
          </div>
        </div>

        <div className="card shadow mb-3">
          <div className="card-body">
          <RcInnerFilter
            initialValues={filters}
            siteOptions={siteOptions}
            onSearch={(values) => {
              setFilters({
                ...values,
                circleId: circleId,
              });

              handleSearch(values);
            }}
            onReset={handleReset}
          />
          </div>
        </div>

        <div className="card shadow">
          <div className="card-body">
            <div className="table-responsive">
              <MultiTable
                needSRNumber
                columns={tableColumns}
                rows={reportData}
                tableHeight={500}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </div>
          </div>
        </div>
      </div>

      <Modal show={!!previewImage} onHide={() => setPreviewImage("")} centered>
        <Modal.Header closeButton>
          <Modal.Title>Image Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <img
            src={previewImage}
            alt="Preview"
            style={{
              width: "100%",
              maxHeight: 400,
              objectFit: "contain",
              borderRadius: 5,
            }}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default RcInnerReport;
